import { Box, Button, Popover, toast } from '@palmetto/palmetto-components';
import type { ButtonTone } from '@palmetto/palmetto-components';
import { type SyntheticEvent, useCallback, useMemo, useState } from 'react';

const POPOVER_CONTAINER_PROPS = {
  padding: 'sm',
  shadow: 'lg',
  radius: 'sm',
  overflow: 'hidden',
  borderColor: 'separator',
  borderWidth: 'xs',
  color: 'body-primary',
  fontSize: 'sm',
  maxWidth: '3xl',
} as const;

function copyLinkToClipboard(link: string) {
  if (!navigator.clipboard) {
    toast.error('Clipboard not available');
    return;
  }
  navigator.clipboard.writeText(link).then(
    () => toast.success('Link copied to clipboard'),
    () => toast.error('Failed to copy link'),
  );
}

interface ActionMenuItem {
  label: string;
  tone: ButtonTone;
  onClick: () => void;
}

export interface PaymentLinkActionMenuProps {
  link?: string;
  isActive: boolean;
  onDeactivate: () => void;
}

export function PaymentLinkActionMenu({ link, isActive, onDeactivate }: PaymentLinkActionMenuProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const toggleMenu = useCallback(() => setIsMenuOpen((prev) => !prev), []);

  const menuItems = useMemo<ActionMenuItem[]>(() => {
    const items: ActionMenuItem[] = [];
    if (link) {
      items.push({
        label: 'Copy Link',
        tone: 'neutral',
        onClick: () => copyLinkToClipboard(link),
      });
    }
    if (isActive) {
      items.push({
        label: 'Deactivate',
        tone: 'danger',
        onClick: onDeactivate,
      });
    }
    return items;
  }, [link, isActive, onDeactivate]);

  if (menuItems.length === 0) return null;

  return (
    <Popover
      trigger="click"
      placement="bottom-end"
      onClickOutside={closeMenu}
      contentContainerProps={POPOVER_CONTAINER_PROPS}
      hasArrow
      isOpen={isMenuOpen}
      content={
        <Box gap="sm" direction="column">
          {menuItems.map((item) => (
            <Button
              key={item.label}
              tone={item.tone}
              onClick={(_event: SyntheticEvent) => {
                item.onClick();
                closeMenu();
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>
      }
    >
      <Button
        iconPrefix="dots"
        variant="tertiary"
        size="md"
        tone="neutral"
        aria-label="Payment link actions"
        onClick={toggleMenu}
      />
    </Popover>
  );
}
